// BUILD-046 Owner-requested layout revision (visual edits ledger, Layout-3 and Layout-9): the ONE aligned task table of every tab.
//
// Columns (Owner's list):  My Tasks and Shared Tasks   Task | Priority | Due | Actions
//                          Team and Company            Task | Assigned to | Priority | Due | Status | Actions
//                          (Compact with "Show requester" on adds a Requested column right after Task on My Tasks, Shared Tasks, Team and Company.)
//                          Assigned by Me (1a)         Task | Assigned to | Status / progress | Priority | Due | Assigned / age | Actions
//                          (Compact with jobs in play adds Customer / Job right after Task in every table; with customers in play that ONE column is headed Customer and also shows the row's job. "Due" is the Task due date: the full name stays in the heading's tooltip and
//                          accessible name and on every form.)
// The header band is pale grey and the rows are white, separated by thin lines. The Task cell is the title (dark, semibold, exactly as entered: never upper-cased or re-cased) and, in Comfortable, ONE quiet second line:
// the short requester badge, then data the task already has (project, source, Waiting / Blocked ...); Priority is a coloured dot AND its word (never colour alone); the Task
// due date is plain text ("Today", "Oct 22") and, when overdue, the word "Overdue" in a darker red; Actions hold the primary button and the row's one "..." menu.
//
// It is a real table to assistive technology (role=table, rowgroup, row, columnheader, cell) built from divs, so the SAME rows can collapse below 768 px into stacked
// rows (title, then priority and due on one quiet line, then the actions) without a horizontal page scroll. A row's panels (a picker, the Done note, History) are a
// full-width row below it. Density: Comfortable has air and the optional detail line; Compact is one tight line. Presentation only: what a row shows and does is the
// caller's.
import { Children } from 'react';
import type { ReactNode } from 'react';
import type { Density } from './densityStore';
import { DUE_COLUMN_LABEL, TASK_DUE_DATE_LABEL } from './dateLabels';
// Cycle A (2026-10-21): the words Today / Yesterday / Tomorrow are written against the page's ONE Phoenix-day signal (usePhoenixDay), not a clock read at render.
import { usePhoenixDay } from './usePhoenixDay';
import { describeDueCell } from './dueLabel';
import { stripTestMarker } from './displayNames';
import { RequestedCell } from './RequesterBadge';
import type { RequesterBadge as RequesterBadgeData } from './requesterBadgeModel';
import { useRequestedColumn } from './useRequestedColumn';
import { JobText } from './JobParts';
import type { JobShown } from './jobDisplayModel';
// BUILD-046 (Customer association): the table still has at most ONE association column. useAssociationColumn says which: the Customer column when customers are in play (its cell also
// shows the row's job on a smaller line), else the original Customer / Job column when jobs are in play, else none.
import { useAssociationColumn } from './useCustomerColumn';
import { associationHeading, CUSTOMER_LABEL, JOB_LABEL, JOB_LINE_PREFIX } from './customerDisplayModel';
import type { CustomerShown } from './customerDisplayModel';
import { CustomerText } from './CustomerParts';
import { describeSortHeading } from './sortHeadingModel';
import type { SortColumn } from './sortHeadingModel';
import { SortHeadingButton, useSortHeadings } from './SortHeadings';
import { TruncatedText } from './TruncatedText';

/** 'team-completed' is the Team / Company table of COMPLETED tasks: the same columns, with an Actions column wide enough for Reopen and the "..." menu on ONE line. */
export type TableVariant = 'mine' | 'team' | 'team-completed' | 'issued';

// Whole class strings (not built from pieces) so the style scanner sees them. The first column takes the slack; the last holds the actions.
const GRID: Readonly<Record<TableVariant, string>> = Object.freeze({
  // BUILD-046 section C: the Actions column of My Tasks is 15rem (was 10rem) so Update, Done and the More menu sit on ONE line, Update beside Done.
  mine: 'md:grid md:grid-cols-[minmax(0,1fr)_6rem_9rem_15rem]',
  // Team table cleanup (Owner, 2026-10-24; Builder 5edc20ea and 93d856a3): Assigned to is a fixed 7rem (it holds one initials circle, or two and "+N", and wraps a third beneath), Due is 5.5rem (an overdue date is
  // STACKED, the word Overdue over the date, so it no longer needs a one-line 9rem), Status is 5rem and Actions 3.75rem (the "..." button and its heading), with 0.5rem between columns. Task takes everything
  // that is left, and never less than 14rem (below that the box scrolls sideways, see TaskTable). Same renderer for Team and Company.
  team: 'md:grid md:grid-cols-[minmax(14rem,1fr)_7rem_5rem_5.5rem_5rem_3.75rem] md:gap-x-2',
  'team-completed': 'md:grid md:grid-cols-[minmax(14rem,1fr)_7rem_5rem_5.5rem_5rem_7.5rem] md:gap-x-2',
  // Increment 1a: Task | Assigned to | Status / progress | Priority | Due | Assigned / age | Actions.
  // Owner direction 2026-10-23 (Assigned by Me): Task is much wider (2.6fr), the Assigned to column hugs its initials circles (7rem, was 10rem), Status is a plain word or a count (8rem, was a 1fr share) and
  // Priority is 5.5rem, so the Status and Priority headings sit apart. Due keeps 8rem for "Overdue - Oct 19"; Assigned / Age keeps 7rem for "Oct 19 - 5 days" on one line; Actions 4.5rem.
  // Task never gets narrower than 14rem: below 61rem of table the box scrolls sideways (see TaskTable) instead of squeezing Task to nothing.
  issued: 'md:grid md:grid-cols-[minmax(14rem,2.6fr)_minmax(0,7rem)_minmax(0,8rem)_5.5rem_8rem_7rem_4.5rem] md:gap-x-3',
});

// The same grids with the "Requested" column (Compact + "Show requester" on) right after the Task column. Header and rows use the same string, so they align.
const GRID_REQUESTED: Readonly<Record<TableVariant, string>> = Object.freeze({
  mine: 'md:grid md:grid-cols-[minmax(0,1fr)_9rem_6rem_9rem_15rem]',
  team: 'md:grid md:grid-cols-[minmax(14rem,1fr)_8rem_7rem_5rem_5.5rem_5rem_3.75rem] md:gap-x-2',
  'team-completed': 'md:grid md:grid-cols-[minmax(14rem,1fr)_8rem_7rem_5rem_5.5rem_5rem_7.5rem] md:gap-x-2',
  // Assigned by Me never has a Requested column (requestedColumnShown); the grid is the plain one.
  issued: 'md:grid md:grid-cols-[minmax(14rem,2.6fr)_minmax(0,7rem)_minmax(0,8rem)_5.5rem_8rem_7rem_4.5rem] md:gap-x-3',
});

// BUILD-046 J1a: the same grids with the "Customer / Job" column (Compact, jobs in play) right after Task, and with both that column and "Requested" (Task | Customer / Job |
// Requested | ...). Whole class strings again. Assigned by Me has no Requested column, so its two job grids are the same.
const GRID_JOB: Readonly<Record<TableVariant, string>> = Object.freeze({
  mine: 'md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,11rem)_6rem_9rem_15rem]',
  // With the association column Task is 32% of the table (never under 14rem) and the association column takes what is left (never under 7rem).
  team: 'md:grid md:grid-cols-[minmax(14rem,32%)_minmax(7rem,1fr)_7rem_5rem_5.5rem_5rem_3.75rem] md:gap-x-2',
  'team-completed': 'md:grid md:grid-cols-[minmax(14rem,32%)_minmax(7rem,1fr)_7rem_5rem_5.5rem_5rem_7.5rem] md:gap-x-2',
  issued: 'md:grid md:grid-cols-[minmax(12rem,2.2fr)_minmax(8rem,1.2fr)_minmax(0,7rem)_minmax(0,8rem)_5.5rem_8rem_7rem_4.5rem] md:gap-x-3',
});

const GRID_REQUESTED_JOB: Readonly<Record<TableVariant, string>> = Object.freeze({
  mine: 'md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,11rem)_9rem_6rem_9rem_15rem]',
  team: 'md:grid md:grid-cols-[minmax(14rem,32%)_minmax(7rem,1fr)_8rem_7rem_5rem_5.5rem_5rem_3.75rem] md:gap-x-2',
  'team-completed': 'md:grid md:grid-cols-[minmax(14rem,32%)_minmax(7rem,1fr)_8rem_7rem_5rem_5.5rem_5rem_7.5rem] md:gap-x-2',
  issued: 'md:grid md:grid-cols-[minmax(12rem,2.2fr)_minmax(8rem,1.2fr)_minmax(0,7rem)_minmax(0,8rem)_5.5rem_8rem_7rem_4.5rem] md:gap-x-3',
});

/** The grid class string for a table with or without the Requested and Customer / Job columns. Header and rows use the same function, so they align. */
function gridFor(variant: TableVariant, requested: boolean, job: boolean): string {
  if (job) return (requested ? GRID_REQUESTED_JOB : GRID_JOB)[variant];
  return (requested ? GRID_REQUESTED : GRID)[variant];
}

/** Team and Company share one renderer (open and completed). */
const isTeamLike = (variant: TableVariant): boolean => variant === 'team' || variant === 'team-completed';

// Team table cleanup: the narrowest the Team / Company table is drawn before its box scrolls sideways inside itself (Task keeps its 14rem and nothing lands on another column or is clipped). Whole class strings.
// Each is the sum of that table's columns at their narrowest, its gaps and the row padding, rounded up by a quarter rem.
const TEAM_MIN_WIDTH: Readonly<Record<'plain' | 'requested' | 'association' | 'both', string>> = Object.freeze({
  plain: 'md:min-w-[45rem]',
  requested: 'md:min-w-[53.5rem]',
  association: 'md:min-w-[52.5rem]',
  both: 'md:min-w-[61rem]',
});
const TEAM_COMPLETED_MIN_WIDTH: Readonly<Record<'plain' | 'requested' | 'association' | 'both', string>> = Object.freeze({
  plain: 'md:min-w-[48.75rem]',
  requested: 'md:min-w-[57.25rem]',
  association: 'md:min-w-[56.25rem]',
  both: 'md:min-w-[64.75rem]',
});

function minWidthFor(variant: TableVariant, requested: boolean, job: boolean): string {
  if (variant === 'issued') return job ? 'md:min-w-[68rem]' : 'md:min-w-[61rem]';
  const key = requested && job ? 'both' : job ? 'association' : requested ? 'requested' : 'plain';
  return (variant === 'team-completed' ? TEAM_COMPLETED_MIN_WIDTH : TEAM_MIN_WIDTH)[key];
}

interface HeaderSpec {
  text: string;
  align?: 'right';
  /** The column this heading sorts (Task, Assigned to, Priority, Due, Status, and the Assigned by Me columns): a button while the page provides its sort state (SortHeadings). Requested and Actions do not sort. */
  sortColumn?: SortColumn;
  /** The heading's full name when `text` is shortened ("Task due date" for "Due"): the tooltip and the accessible name of a plain heading carry it (a sorting heading's name comes from sortHeadingModel). */
  fullText?: string;
  /** A note of this table's own for the heading's accessible name (Assigned by Me: an Everyone request carries no due date or priority in the list and is last). */
  sortNote?: string;
  /** The heading drawn on two lines (Assigned by Me: "Assigned /" over "Age"). The text above stays the one name; the lines are only how it is drawn. */
  lines?: readonly string[];
}

// Increment 1a: the Task due date column is headed "Due" in EVERY task table; the full name "Task due date" stays in the tooltip, the accessible name and every form.
const DUE_HEADER: HeaderSpec = { text: DUE_COLUMN_LABEL, fullText: TASK_DUE_DATE_LABEL, sortColumn: 'due' };

const TEAM_HEADERS: readonly HeaderSpec[] = [
  { text: 'Task', sortColumn: 'title' },
  { text: 'Assigned to', sortColumn: 'assignee' },
  { text: 'Priority', sortColumn: 'priority' },
  DUE_HEADER,
  { text: 'Status', sortColumn: 'status' },
  { text: 'Actions', align: 'right' },
];

// Assigned by Me (increment 1a): what did I request, who is responsible now, its status, when it is due, when it was assigned and how old it is. The Everyone caveats are in the
// sorting headings' names, so a person who sorts by Priority or Due is told where an Everyone request goes.
const ISSUED_HEADERS: readonly HeaderSpec[] = [
  { text: 'Task', sortColumn: 'title' },
  { text: 'Assigned to', sortColumn: 'responsible' },
  { text: 'Status', sortColumn: 'progress' },
  { text: 'Priority', sortColumn: 'priority', sortNote: 'An Everyone request is ordered by the priority all its recipients share; one where they differ is last' },
  { ...DUE_HEADER, sortNote: 'An Everyone request is ordered by the due date all its recipients share; one where they differ is last' },
  { text: 'Assigned / Age', lines: ['Assigned /', 'Age'], sortColumn: 'assigned' },
  { text: 'Actions', align: 'right' },
];

const HEADERS: Readonly<Record<TableVariant, readonly HeaderSpec[]>> = Object.freeze({
  mine: [{ text: 'Task', sortColumn: 'title' }, { text: 'Priority', sortColumn: 'priority' }, DUE_HEADER, { text: 'Actions', align: 'right' }],
  team: TEAM_HEADERS,
  'team-completed': TEAM_HEADERS,
  issued: ISSUED_HEADERS,
});

/**
 * The headings of a table: the association heading right after Task when the table has that column ("Customer" when customers are in play, else the original "Customer / Job"),
 * then "Requested" when it has that one.
 */
function headersFor(variant: TableVariant, requested: boolean, job: boolean = false, associationText: string = 'Customer / Job'): readonly HeaderSpec[] {
  const base = HEADERS[variant];
  if (!requested && !job) return base;
  return [base[0], ...(job ? [{ text: associationText }] : []), ...(requested ? [{ text: 'Requested' }] : []), ...base.slice(1)];
}

export interface TaskTableProps {
  variant: TableVariant;
  /** The table's accessible name. */
  ariaLabel: string;
  children: ReactNode;
}

export function TaskTable({ variant, ariaLabel, children }: TaskTableProps) {
  const requested = useRequestedColumn(variant);
  // The ONE association column (BUILD-046): "jobColumn" is whether the table has it - the Customer / Job column it grew from, now headed "Customer" while customers are in play.
  const association = useAssociationColumn();
  const jobColumn = association !== 'none';
  // Team and Company write the JOB first in the association cell, so the heading says so (Job / Customer); every other table keeps "Customer".
  const headers = headersFor(variant, requested, jobColumn, associationHeading(association, isTeamLike(variant)) ?? undefined);
  const sorting = useSortHeadings();
  // Owner direction 2026-10-23 (Assigned by Me): the table has seven columns of fixed room plus Task. Where the window leaves it less than 61rem (68rem with the Customer / Job column) the box scrolls
  // sideways inside itself - Task keeps its 14rem and no heading or cell lands on another - instead of Task being squeezed to nothing. The inner box has no role, so the table's rows stay its direct structure.
  // Team table cleanup: Team and Company scroll the same way (they used to squeeze Task to nothing and clip); My Tasks and Shared Tasks are unchanged. Their box is also `relative`, so the screen-reader-only
  // words of a cell scrolled out of view (position: absolute) are contained and clipped by the box instead of widening the whole page at narrow windows.
  const scrolls = variant === 'issued' || isTeamLike(variant);
  const inner = (
    <>
      <div role="rowgroup" className="hidden md:block">
        <div role="row" className={`${gridFor(variant, requested, jobColumn)} items-end gap-x-4 border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600`}>
          {headers.map((header) => {
            // A sortable heading is a button, with aria-sort, only while the page provides its sort state and the mode it selects is offered; else it is plain text.
            const sort = sorting !== null && header.sortColumn !== undefined ? describeSortHeading(header.sortColumn, sorting.mode, sorting.available, header.sortNote) : null;
            return (
              <div key={header.text} role="columnheader" aria-sort={sort !== null && sort.clickable ? sort.ariaSort : undefined} className={`whitespace-nowrap ${header.align === 'right' ? 'text-right' : ''}`}>
                {sort !== null && sort.clickable && sorting !== null ? (
                  <SortHeadingButton heading={sort} onSelect={sorting.select} />
                ) : header.lines !== undefined ? (
                  <span>
                    <span className="sr-only">{header.text}</span>
                    {header.lines.map((line) => (
                      <span key={line} aria-hidden="true" className="block">
                        {line}
                      </span>
                    ))}
                  </span>
                ) : header.fullText !== undefined ? (
                  // A plain "Due" heading keeps the full name for hover and for assistive technology.
                  <span title={header.fullText}>
                    {header.text}
                    <span className="sr-only"> ({header.fullText})</span>
                  </span>
                ) : (
                  header.text
                )}
              </div>
            );
          })}
        </div>
      </div>
      {children}
    </>
  );
  return (
    <div role="table" aria-label={ariaLabel} aria-colcount={headers.length} className={`${scrolls ? 'overflow-x-auto' : 'overflow-hidden'} ${isTeamLike(variant) ? 'relative ' : ''}rounded-lg border border-gray-200 bg-white`} data-testid="task-table">
      {scrolls ? (
        <div role="presentation" className={minWidthFor(variant, requested, jobColumn)}>
          {inner}
        </div>
      ) : (
        inner
      )}
    </div>
  );
}

export interface TaskTableRowProps {
  variant: TableVariant;
  id?: string;
  testId?: string;
  density: Density;
  highlight?: boolean;
  /** The cells, in column order, each a <TaskCell>, <Cell> or <ActionsCell>. The FIRST must be the Task cell (the "Customer / Job" and "Requested" cells are inserted after it). */
  children: ReactNode;
  /** The requester badge of the row. It is drawn in the "Requested" column when the table has one (Compact + "Show requester" on); null leaves that cell empty. */
  requested?: RequesterBadgeData | null;
  /** The job of the row (BUILD-046 J1a). It is drawn in the "Customer / Job" column when the table has one (Compact, jobs in play); null leaves that cell empty. */
  job?: JobShown | null;
  /** The customer of the row (BUILD-046 Customer association). It is drawn in the association column when customers are in play ("Customer"), with the row's job on a smaller line under it; null leaves the customer line empty. */
  customer?: CustomerShown | null;
  /** Full-width panels the row opens (a picker, the Done note, History), notices. */
  below?: ReactNode;
  /** How many columns the table has (for the full-width row below). */
  columns?: number;
  /** Team and Company: the association cell writes the job first, then the customer (BUILD-046 Team table cleanup). Every other table leaves it off and is unchanged. */
  jobFirst?: boolean;
}

export function TaskTableRow({ variant, id, testId, density, highlight = false, children, requested, job, customer, below, columns, jobFirst = false }: TaskTableRowProps) {
  const compact = density === 'compact';
  // Team and Company rows in Comfortable are a little tighter (py-2 instead of py-3): the title and its context line keep their own spacing, only the air around the row is reduced.
  const rowPadding = compact ? 'py-1.5' : isTeamLike(variant) ? 'py-2' : 'py-3';
  const requestedColumn = useRequestedColumn(variant);
  const association = useAssociationColumn();
  const jobColumn = association !== 'none';
  const span = columns ?? headersFor(variant, requestedColumn, jobColumn).length;
  const cells = requestedColumn || jobColumn ? Children.toArray(children) : null;
  return (
    <div role="rowgroup" id={id} data-testid={testId} data-density={density} className={`border-b border-gray-200 last:border-b-0 ${highlight ? 'bg-sky-50 ring-2 ring-inset ring-[#4AABDE]' : 'hover:bg-gray-50/60'}`}>
      <div role="row" className={`flex flex-wrap items-center gap-x-4 gap-y-1 px-4 ${rowPadding} ${gridFor(variant, requestedColumn, jobColumn)}`}>
        {cells === null ? (
          children
        ) : (
          <>
            {cells[0]}
            {association === 'customer' ? <CustomerCell customer={customer ?? null} job={job ?? null} jobFirst={jobFirst} /> : jobColumn && <JobCell job={job ?? null} />}
            {requestedColumn && <RequestedCell badge={requested ?? null} />}
            {cells.slice(1)}
          </>
        )}
      </div>
      {below !== undefined && below !== null && below !== false && (
        <div role="row">
          <div role="cell" aria-colspan={span} className="px-4 pb-3">
            {below}
          </div>
        </div>
      )}
    </div>
  );
}

export interface TaskCellProps {
  title: string;
  /** The ONE quiet second line (project, source, status word, who asked). Null or '' for none. */
  secondary?: ReactNode;
  /** Comfortable only: description, Next action, follow-up date. */
  detail?: ReactNode;
  density: Density;
  /** Extra quiet text for the stacked layout only is not needed: the same line is used. */
  titleSuffix?: ReactNode;
  /**
   * Comfortable only: the small requester badge ("By Julie · Oct 18"), FIRST on the second line, below the title. Compact never draws it beside the title (Compact
   * shows it in its own "Requested" column when the person turned "Show requester" on).
   */
  badge?: ReactNode;
  /**
   * Team and Company, Compact only (BUILD-046 Team table cleanup): the quiet part goes on its OWN line under the title, wrapping instead of being cut, and only when the row has one. Beside a long title
   * the one-line form gets no room at all, which hid a follow-up warning along with the repeated word Overdue. A row with nothing to say stays one tight line. Every other table leaves it off.
   */
  secondaryBelow?: boolean;
}

export function TaskCell({ title: storedTitle, secondary, detail, density, titleSuffix, badge, secondaryBelow = false }: TaskCellProps) {
  // D1 display cleanup: the title is SHOWN without the "(FICTIONAL)" test marker (hover text included); the stored title, search and sort are not changed.
  const title = stripTestMarker(storedTitle);
  const compact = density === 'compact';
  const hasSecondary = secondary !== undefined && secondary !== null && secondary !== '' && secondary !== false;
  const hasBadge = badge !== undefined && badge !== null && badge !== false;
  if (compact) {
    return (
      <div role="cell" className="min-w-0 basis-full md:basis-auto">
        <div className="flex min-w-0 items-baseline gap-x-2">
          <TruncatedText text={title} className="text-sm font-semibold text-gray-900" />
          {titleSuffix}
          {hasSecondary && !secondaryBelow && <span className="min-w-0 flex-1 truncate text-xs text-gray-600">{secondary}</span>}
        </div>
        {hasSecondary && secondaryBelow && (
          <div className="mt-0.5 min-w-0 break-words text-xs text-gray-600" data-testid="task-secondary-compact">
            {secondary}
          </div>
        )}
      </div>
    );
  }
  return (
    <div role="cell" className="min-w-0 basis-full md:basis-auto">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="break-words text-sm font-semibold text-gray-900">{title}</span>
        {titleSuffix}
      </div>
      {(hasBadge || hasSecondary) && (
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600" data-testid="task-secondary">
          {hasBadge && badge}
          {hasSecondary && <span className="min-w-0">{secondary}</span>}
        </div>
      )}
      {detail !== undefined && detail !== null && <div className="mt-1 text-xs text-gray-600">{detail}</div>}
    </div>
  );
}

export interface CellProps {
  children: ReactNode;
  /** Shown before the content in the stacked layout only (the column header is not visible there). */
  stackedLabel?: string;
  /** Grow to its natural width in the stacked layout and keep the column's cell otherwise. */
  className?: string;
  /** The full text as a hover tooltip, for a cell that Compact cuts short with an ellipsis. */
  title?: string;
}

/** An ordinary cell (priority, due date, assignee, status ...). */
export function Cell({ children, stackedLabel, className, title }: CellProps) {
  return (
    <div role="cell" title={title} className={`min-w-0 text-sm text-gray-800 ${className ?? ''}`}>
      {stackedLabel !== undefined && <span className="mr-1 text-xs text-gray-600 md:hidden">{stackedLabel}</span>}
      {children}
    </div>
  );
}

/**
 * The "Customer / Job" cell of Compact (BUILD-046 J1a): the job name on one line, cut with an ellipsis (the full name is the hover text), with the quiet note when the
 * name is the last known one, the job was not found or it is archived. A row with no job shows a dash in the table and nothing in the stacked layout, so a phone-width
 * row is not padded with empty labels.
 */
export function JobCell({ job }: { job: JobShown | null }) {
  if (job === null) {
    return (
      <div role="cell" className="hidden min-w-0 text-sm text-gray-600 md:block" data-testid="job-cell">
        <span aria-hidden="true">{'\u2014'}</span>
        <span className="sr-only">No job</span>
      </div>
    );
  }
  return (
    <div role="cell" className="min-w-0 text-sm text-gray-800" data-testid="job-cell">
      <span className="mr-1 text-xs text-gray-600 md:hidden">Customer / Job</span>
      <JobText job={job} truncate />
    </div>
  );
}

/**
 * The "Customer" cell of Compact while customers are in play (BUILD-046 Customer association): the customer on one line (cut with an ellipsis, the full name on hover and on focus), with the
 * quiet note when the name is the last known one, the customer was not found or it is inactive - and, when the row ALSO carries a job, a smaller "Job: <name>" line under it. The job is the
 * unchanged job of the row (same name, same notes as the "Customer / Job" column); a customer never reads or writes it. A row with neither shows a dash in the table and nothing in the stacked
 * layout; a row whose customer is missing but which carries a job shows only the job line.
 */
export function CustomerCell({ customer, job, jobFirst = false }: { customer: CustomerShown | null; job: JobShown | null; jobFirst?: boolean }) {
  // Team and Company (BUILD-046 Team table cleanup): the JOB first, the customer second, each line saying which it is ("Job:" / "Customer:"), because the heading ("Job / Customer") covers both and a
  // row may carry only one of them. Same names, same notes (last known name, not found, inactive / archived) and the same full-name tooltip and keyboard focus as every other cell.
  if (jobFirst && (customer !== null || job !== null)) return <JobFirstCell customer={customer} job={job} />;
  if (customer === null && job === null) {
    return (
      <div role="cell" className="hidden min-w-0 text-sm text-gray-600 md:block" data-testid="customer-cell">
        <span aria-hidden="true">{'\u2014'}</span>
        <span className="sr-only">No customer</span>
      </div>
    );
  }
  return (
    <div role="cell" className="min-w-0 text-sm text-gray-800" data-testid="customer-cell">
      {/* CHANGED 2026-10-25: the stacked-layout "Customer" label is drawn only when the row HAS a customer; a job-only row is self-labelled "Job:" and must not also say "Customer". */}
      {customer !== null && <span className="mr-1 text-xs text-gray-600 md:hidden">Customer</span>}
      {customer !== null && <CustomerText customer={customer} truncate />}
      {job !== null && (
        <div className="flex min-w-0 items-baseline gap-1 text-xs text-gray-600" data-testid="customer-cell-job">
          <span className="flex-shrink-0">{JOB_LINE_PREFIX.trim()}</span>
          <span className="min-w-0 flex-1">
            <JobText job={job} truncate />
          </span>
        </div>
      )}
    </div>
  );
}

/** One labelled line of the Team association cell: a small label that never shrinks, then the value, cut with an ellipsis (its full name is on hover and on keyboard focus). */
function LabelledLine({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-baseline gap-1" data-testid={testId}>
      <span className="flex-shrink-0 text-xs text-gray-600">{label}</span>
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  );
}

/** The Team / Company association cell: the job, then the customer, each only when the row has it. */
function JobFirstCell({ customer, job }: { customer: CustomerShown | null; job: JobShown | null }) {
  return (
    <div role="cell" className="min-w-0 text-sm text-gray-800" data-testid="customer-cell" data-order="job-first">
      {/* each line says which it is, so the stacked (phone) layout needs no extra "Job / Customer" label above them */}
      {job !== null && (
        <LabelledLine label={JOB_LABEL} testId="customer-cell-job">
          <JobText job={job} truncate />
        </LabelledLine>
      )}
      {customer !== null && (
        <LabelledLine label={CUSTOMER_LABEL} testId="customer-cell-customer">
          <CustomerText customer={customer} truncate />
        </LabelledLine>
      )}
    </div>
  );
}

/** The actions cell: right-aligned in the table, at the right end of the stacked row. */
export function ActionsCell({ children }: { children?: ReactNode }) {
  return (
    <div role="cell" className="ml-auto flex flex-wrap items-center justify-end gap-1.5 md:ml-0">
      {children}
    </div>
  );
}

const PRIORITY_DOT: Readonly<Record<string, string>> = Object.freeze({
  urgent: 'bg-red-600',
  high: 'bg-orange-500',
  medium: 'bg-amber-400',
  low: 'bg-green-500',
});

/** A coloured dot AND the word: the colour is never the only signal. */
export function PriorityMark({ priority }: { priority: string | null | undefined }) {
  if (typeof priority !== 'string' || priority.trim() === '') {
    return (
      <span className="text-gray-600">
        <span aria-hidden="true">{'\u2014'}</span>
        <span className="sr-only">No priority</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span aria-hidden="true" className={`h-2 w-2 flex-shrink-0 rounded-full ${PRIORITY_DOT[priority] ?? 'bg-gray-400'}`} />
      <span className="capitalize">{priority}</span>
    </span>
  );
}

const STATUS_MARKS: Readonly<Record<string, { label: string; tone: string }>> = Object.freeze({
  in_progress: { label: 'In progress', tone: 'border-sky-300 bg-sky-50 text-sky-900' },
  waiting: { label: 'Waiting', tone: 'border-amber-300 bg-amber-50 text-amber-900' },
  blocked: { label: 'Blocked', tone: 'border-red-300 bg-red-50 text-red-900' },
});

/**
 * 2026-10-24 Owner correction 1: a concise status word beside the title, so a mixed-status list (All open, Overdue) tells In progress, Waiting and Blocked apart. Active is the plain case and
 * carries none. The word is text with a border, never colour alone. The underlying status names are unchanged.
 */
export function StatusMark({ status }: { status: unknown }) {
  const mark = typeof status === 'string' ? STATUS_MARKS[status] : undefined;
  if (mark === undefined) return null;
  return (
    <span className={`inline-flex flex-shrink-0 items-center rounded border px-1.5 py-px text-[11px] font-medium ${mark.tone}`} data-testid="status-mark" data-status={String(status)}>
      {mark.label}
    </span>
  );
}

/**
 * The Task due date, written by dueLabel.ts. `overdue` is the caller's answer from THE one overdue rule (overdueRule.ts), passed through; the day is the page's signal.
 * `stack`: an overdue date is drawn as the word "Overdue" above its date (Comfortable everywhere; Team and Company also in Compact, where the Due column is narrow). `tight` (Team and Company only)
 * closes the line spacing of the two stacked lines so a stacked row is not much taller than a one-line row.
 */
export function DueMark({ dueDate, overdue, stack = false, tight = false }: { dueDate: unknown; overdue: boolean; stack?: boolean; tight?: boolean }) {
  const today = usePhoenixDay();
  const cell = describeDueCell({ dueDate, overdue }, today);
  if (cell.empty) {
    return (
      <span className="text-gray-600" title={cell.title}>
        <span aria-hidden="true">{cell.text}</span>
        <span className="sr-only">{cell.title}</span>
      </span>
    );
  }
  return (
    <span className={`whitespace-nowrap ${cell.overdue ? 'font-medium text-red-700' : 'text-gray-800'}`} title={cell.title}>
      {stack && cell.overdue && cell.text.includes(' \u00b7 ') ? (
        <>
          <span className={tight ? 'block leading-tight' : 'block'}>{cell.text.split(' \u00b7 ')[0]}</span>
          <span className={tight ? 'block leading-tight' : 'block'}>{cell.text.split(' \u00b7 ').slice(1).join(' \u00b7 ')}</span>
        </>
      ) : (
        cell.text
      )}
    </span>
  );
}

/**
 * The quiet line for `parts`, or `undefined` when every part is empty, so the Task cell draws NO line (and no gap) for it. A `<QuietLine>` element is always "something" to the cell even when it
 * renders nothing, which left an empty line under the title of a row with nothing to say. Used by Team and Company (BUILD-046 Team table cleanup: no placeholder blank lines).
 */
export function quietLineOf(parts: readonly ReactNode[]): ReactNode {
  return parts.some((part) => part !== null && part !== undefined && part !== false && part !== '') ? <QuietLine parts={parts} /> : undefined;
}

/** Joins the parts of the quiet second line with a middle dot, skipping empty ones. Returns null when nothing is left. */
export function QuietLine({ parts }: { parts: readonly ReactNode[] }) {
  const kept = parts.filter((part) => part !== null && part !== undefined && part !== false && part !== '');
  if (kept.length === 0) return null;
  return (
    <>
      {kept.map((part, index) => (
        <span key={index}>
          {index > 0 && (
            <span aria-hidden="true" className="px-1.5 text-gray-600">
              {'\u2022'}
            </span>
          )}
          {part}
        </span>
      ))}
    </>
  );
}
